import React, { useState, useEffect } from "react";
import { getAvailableModels, type AgentModelOption } from "../../../../services/agentService";
import { useAuth } from "../../../../context/AuthContext";
import { debug } from "../../../../utils/debugLogger";
import {
  isRetiredModel,
  isSafetyReviewModel,
  recommendedModelFor,
  SAFETY_REVIEW_NOTICE,
} from "../../ChatWithAgent/config/modelOptions";

interface ModelSelectionSectionProps {
  selectedModel: string;
  /**
   * The Agent's type. With `lockProvider`, decides which model is labelled recommended: Sonnet 5,
   * or Opus 5 for the AI Visibility Manager (DEFAULT_MODEL_BY_AGENT_TYPE); GPT-5.6 Terra for an
   * Agent locked to OpenAI. Unset (e.g. the create form before a role is chosen) recommends
   * Sonnet 5.
   */
  agentType?: string | null;
  /** Called with the model id and, when known, the full option (so callers can
   *  keep `provider` in step with the model on creation). */
  onModelChange: (model: string, option?: AgentModelOption) => void;
  disabled?: boolean;
  /**
   * When set, only models on this provider are selectable. An existing agent's
   * provider decides which runtime holds its knowledge files and ids
   * (`bedrock_knowledge_base_id` vs `vector_store_id`), so switching provider is
   * not a model swap — the other provider's models stay visible but disabled
   * with a note, rather than silently breaking the agent (SGL-257).
   */
  lockProvider?: string;
  /** Tailwind classes for the <select>; defaults match the create/edit forms. */
  className?: string;
}

const ModelSelectionSection: React.FC<ModelSelectionSectionProps> = ({
  selectedModel,
  agentType,
  onModelChange,
  disabled = false,
  lockProvider,
  className,
}) => {
  const { idToken } = useAuth();
  const [models, setModels] = useState<AgentModelOption[]>([]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const fetchedModels = await getAvailableModels(idToken);
        setModels(fetchedModels);
      } catch (error) {
        debug.error("Error fetching models:", error);
        setModels([]);
      }
    };
    fetchModels();
  }, [idToken]);

  const recommended = recommendedModelFor(agentType, lockProvider);

  // Retired models are never offered (SGL-423); an agent that still stores one is handled
  // below and, at chat time, swapped for a current model without the owner doing anything.
  const offered = models.filter((m) => !isRetiredModel(m.id));

  // Group by provider so the picker reads "Claude / GPT", and so a locked provider
  // can be shown as one disabled group instead of scattered greyed rows.
  const groups = offered.reduce<Record<string, AgentModelOption[]>>((acc, m) => {
    (acc[m.providerLabel] ||= []).push(m);
    return acc;
  }, {});

  // An agent created before this picker existed may carry a model id that is no
  // longer in the list. Show it as the current value instead of a blank select.
  const knownIds = new Set(offered.map((m) => m.id));
  const showLegacy = selectedModel && models.length > 0 && !knownIds.has(selectedModel);

  const lockedLabel = lockProvider
    ? models.find((m) => m.provider === lockProvider)?.providerLabel
    : undefined;

  return (
    <div className="mb-6">
      <label htmlFor="agent-model" className="block text-gray-700 font-medium mb-2">
        Model
      </label>
      <select
        id="agent-model"
        value={selectedModel}
        onChange={(e) => {
          const id = e.target.value;
          onModelChange(id, models.find((m) => m.id === id));
        }}
        disabled={disabled || models.length === 0}
        className={
          className ??
          "w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-60"
        }
      >
        {!selectedModel && <option value="">Select a model</option>}
        {showLegacy && (
          <option value={selectedModel}>
            {isRetiredModel(selectedModel)
              ? `Current: ${selectedModel} (retired, replaced automatically)`
              : `Current: ${selectedModel}`}
          </option>
        )}
        {Object.entries(groups).map(([label, opts]) => {
          const locked = !!lockProvider && opts[0]?.provider !== lockProvider;
          return (
            <optgroup
              key={label}
              label={locked ? `${label} (create a new agent to use these)` : label}
            >
              {/* An <option> cannot contain markup, so the tier hint renders as plain
                  text rather than as the badge used in the chat picker. A model the
                  agent's provider locks out is never called recommended. */}
              {opts.map((model) => {
                const tag =
                  model.id === recommended.model && !locked ? "recommended" : model.hint;
                return (
                  <option key={model.id} value={model.id} disabled={locked}>
                    {model.name}
                    {tag ? ` — ${tag}` : ""}
                  </option>
                );
              })}
            </optgroup>
          );
        })}
      </select>
      <p className="mt-2 text-sm text-gray-500">
        {lockedLabel
          ? `This agent runs on ${lockedLabel}. You can switch between ${lockedLabel} models; to use a different provider, create a new agent. Recommended for this agent: ${recommended.label}.`
          : `The model that powers this agent. Recommended for this agent: ${recommended.label}.`}
      </p>
      {isSafetyReviewModel(selectedModel) && (
        <p className="mt-1 text-sm text-gray-500">{SAFETY_REVIEW_NOTICE}</p>
      )}
    </div>
  );
};

export default ModelSelectionSection;
